import { isTrialAllowedRcid } from '../trial/trialCompanyAllowList';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type PrimarySelection,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';

/**
 * A selection's rcid is the trailing segment of its depth-compound id
 * (`8_256_1116644` -> `1116644`). AI-resolved selections can arrive before
 * `resolveCompanyNamesFromApi` patches them, carrying the rcid in `name`
 * instead — the same fallback `collectUnresolvedCompanyRcids` relies on.
 * Returns null when neither route yields one, so the caller can fail closed.
 */
const getCompanyRcid = (selection: TreeSelection): string | null => {
  const fromId = selection.id.split('_').pop();
  if (fromId && /^\d+$/.test(fromId)) return fromId;
  if (/^\d+$/.test(selection.name)) return selection.name;
  return null;
};

/**
 * The entitlement is on named COMPANIES, not on breadth of analysis: a trial
 * user may select any industry, so rollups (rics_k10 / k50 / k400) pass through
 * untouched and only company-level selections are checked against the plan.
 * Fails closed when a company selection yields no readable rcid.
 */
const isAllowed = (selection: TreeSelection): boolean => {
  if (selection.levelId !== 'company') return true;
  const rcid = getCompanyRcid(selection);
  return rcid !== null && isTrialAllowedRcid(rcid);
};

type Restricted<T extends TreeSelection> = {
  kept: T[];
  blockedNames: string[];
};

const partition = <T extends TreeSelection>(
  selections: readonly T[]
): Restricted<T> => {
  const kept: T[] = [];
  const blockedNames: string[] = [];
  for (const selection of selections) {
    if (isAllowed(selection)) {
      kept.push(selection);
    } else {
      blockedNames.push(selection.name);
    }
  }
  return { kept, blockedNames };
};

/**
 * Strips a tree filter down to allowed companies. Returns null when nothing
 * survives, which tells the caller to drop the filter entirely rather than
 * apply an empty one — see `restrictCompanySelectionsToAllowList`.
 */
const restrictTreeFilter = (
  filter: TreeFilter
): { filter: TreeFilter | null; blockedNames: string[] } => {
  const selections = partition<TreeSelection>(filter.selections);
  const primary = partition<PrimarySelection>(filter.primarySelections ?? []);
  const blockedNames = [...selections.blockedNames, ...primary.blockedNames];

  if (blockedNames.length === 0) {
    return { filter, blockedNames };
  }

  const hadPrimary = (filter.primarySelections?.length ?? 0) > 0;
  const isEmpty =
    selections.kept.length === 0 && (!hadPrimary || primary.kept.length === 0);

  if (isEmpty) {
    return { filter: null, blockedNames };
  }

  return {
    filter: {
      ...filter,
      selections: selections.kept,
      ...(hadPrimary && { primarySelections: primary.kept }),
    },
    blockedNames,
  };
};

/**
 * Removes company selections a self-signup trial user is not entitled to from
 * an AI-produced filter set.
 *
 * A filter left with no surviving selections is DROPPED rather than applied
 * empty: `useApplyAiSearchFilters` seeds its base map with the full default
 * company set and only overwrites the ids this returns, so dropping the filter
 * leaves all five defaults standing instead of collapsing the page to a single
 * company (or to no unit of analysis at all, which the pages do not support).
 *
 * Containment for the AI path only — hand-edited URL params, saved pages and
 * direct API calls still reach any company. Real enforcement belongs in go-api
 * against the client's `company_lists`.
 */
export const restrictCompanySelectionsToAllowList = (
  filters: Filter[]
): { filters: Filter[]; blockedNames: string[] } => {
  const blockedNames: string[] = [];
  const restricted: Filter[] = [];

  for (const filter of filters) {
    if (filter.id === FilterId.COMPANY && filter.type === FilterType.TREE) {
      const result = restrictTreeFilter(filter);
      blockedNames.push(...result.blockedNames);
      if (result.filter) restricted.push(result.filter);
      continue;
    }

    if (filter.id === FilterId.MULTI_COMPANY && isMultiFilter(filter)) {
      const instances: TreeFilter[] = [];
      for (const instance of filter.instances) {
        if (instance.type !== FilterType.TREE) continue;
        const result = restrictTreeFilter(instance);
        blockedNames.push(...result.blockedNames);
        if (result.filter) instances.push(result.filter);
      }
      if (instances.length > 0) {
        restricted.push({ ...filter, instances });
      }
      continue;
    }

    restricted.push(filter);
  }

  return { filters: restricted, blockedNames: [...new Set(blockedNames)] };
};
